import { ChangeDetectionStrategy, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslatePipe } from '@ngx-translate/core';
import { IResidentS3File, TicketDetailsOverviewMetadataBaseComponent } from '@resident-nx/shared';
import { LabelWebComponent } from '../../../../atoms/label/label.component';
import { DefaultValuePipe, IconWebComponent } from '@resident-nx/web/ui';
import { ImmomioButtonComponent } from '@immomio/ui/button';
import {
  TicketAttachmentLoadedEvent,
  TicketImageViewerComponent,
} from '../../../../atoms/ticket-image-viewer/ticket-image-viewer.component';
import { FileDownloadWebService } from '../../../../../services';
import { PdfAttachmentComponent } from '../../../../molecules/pdf-attachment/pdf-attachment.component';
import { Dialog } from '@angular/cdk/dialog';
import {
  FilePreviewModalComponent,
  FilePreviewModalData,
  FilePreviewModalFile,
} from '@resident-nx/web/ui';
import { DatePipe } from '@angular/common';

@Component({
  standalone: true,
  selector: 'rs-web-ticket-details-overview-metadata',
  templateUrl: './ticket-details-overview-metadata.component.html',
  styleUrls: ['./ticket-details-overview-metadata.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslatePipe,
    IconWebComponent,
    LabelWebComponent,
    ImmomioButtonComponent,
    TicketImageViewerComponent,
    PdfAttachmentComponent,
    DefaultValuePipe,
    DatePipe,
  ],
})
export class TicketDetailsOverviewMetadataWebComponent extends TicketDetailsOverviewMetadataBaseComponent {
  #downloadService = inject(FileDownloadWebService);
  #dialog = inject(Dialog);
  #destroyRef = inject(DestroyRef);
  #sanitizer = inject(DomSanitizer);
  #loadedAttachments = new Map<string, TicketAttachmentLoadedEvent>();

  onAttachmentLoaded(event: TicketAttachmentLoadedEvent): void {
    this.#loadedAttachments.set(event.identifier, event);
  }

  openFilePreview(ticketId: string, files: IResidentS3File[], identifier: string): void {
    const previewFiles: FilePreviewModalFile[] = files.map(file => {
      const cached = this.#loadedAttachments.get(file.identifier);
      if (cached) {
        return {
          identifier: file.identifier,
          name: file.name,
          extension: file.extension,
          fileUrl: cached.url ? this.#sanitizer.bypassSecurityTrustResourceUrl(cached.url) : null,
          downloadUrl: cached.url,
          isRequested: cached.isRequested,
          isAlreadyRequested: cached.isAlreadyRequested,
        };
      }
      return {
        identifier: file.identifier,
        name: file.name,
        extension: file.extension,
      };
    });
    const startIndex = previewFiles.findIndex(file => file.identifier === identifier);

    const data: FilePreviewModalData = {
      files: previewFiles,
      startIndex,
    };
    const dialogRef = this.#dialog.open(FilePreviewModalComponent, {
      data,
    });

    const modalComponent = dialogRef.componentInstance as FilePreviewModalComponent;

    const uncachedFiles = previewFiles.filter(file => !file.fileUrl);
    this.prepareTicketFileUrls(ticketId, uncachedFiles, dialogRef);

    modalComponent.download.subscribe(fileToDownload => {
      this.#downloadService.download(fileToDownload.downloadUrl, fileToDownload.name);
    });
  }

  private prepareTicketFileUrls(
    ticketId: string,
    files: FilePreviewModalFile[],
    dialogRef: ReturnType<Dialog['open']>
  ): void {
    for (const file of files) {
      this.#downloadService
        .downloadTicketAttachment(ticketId, file.identifier)
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe(result => {
          const modalComponent = dialogRef.componentInstance as FilePreviewModalComponent;
          modalComponent.files.update(currentFiles =>
            currentFiles.map(f => {
              if (f.identifier === file.identifier) {
                const fileUrl = result.url
                  ? this.#sanitizer.bypassSecurityTrustResourceUrl(result.url)
                  : null;
                return {
                  ...f,
                  fileUrl,
                  downloadUrl: result.url,
                  isRequested: result.isRequested,
                  isAlreadyRequested: result.isAlreadyRequested,
                  isUnavailable: result.isUnavailable,
                };
              }
              return f;
            })
          );
        });
    }
  }
}
